import React, { useCallback, useMemo, useState } from "react";
import Dialog from "../../../components/dialog/dialog";
import { Box, Checkbox, CircularProgress, Typography } from "@mui/material";
import { Button } from "../../../components";
import Textarea from "../../../components/textarea/textarea";
import { post } from "../../../api/setting/actions";
import { useSelector } from "react-redux";
import { getUid } from "../../../redux/authSlice";
import SoldReview from "../../../components/sold-review/sold-review";

interface AppointmentCarsProps {
  customerId: string;
  vin: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  appointmentId: string;
  notificationId?: string;
  notShowUp?: boolean;
}

const AppointmentCars: React.FC<AppointmentCarsProps> = ({
  open,
  setOpen,
  customerId,
  appointmentId,
  notificationId,
  notShowUp,
}) => {
  const [state, setState] = useState<"purchase" | "follow-up" | "select-car">(
    notShowUp ? "follow-up" : "purchase"
  );
  const [text, setText] = useState<string>("");
  const [isSendReview, setIsSendReview] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const uid = useSelector(getUid);

  const onClose = useCallback(() => {
    setState("purchase");
    setOpen(false);
  }, [setOpen]);

  const handleTextChange = useCallback((e: any) => {
    setText(e.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (submitLoading) return;
    setSubmitLoading(true);
    // const selectedCar = cars.find((car) => car.vin === selectedVin);
    const data = {
      uid,
      //   ...(selectedCustomerID && { customerId: selectedCustomerID }),
      //   ...(!selectedCustomerID && {
      //     customerData: {
      //       firstName: getValues("firstName"),
      //       lastName: getValues("lastName"),
      //       email: getValues("email"),
      //       phoneNumber:
      //         String(getValues("phoneNumber")).length > 2 ? getValues("phoneNumber") : null,
      //     },
      //   }),
      //   carData: {
      //     // vin: selectedVin,
      //     year: selectedCar?.year,
      //     make: selectedCar?.make,
      //     model: selectedCar?.model,
      //     reviewType: "30_day",
      //     reviewStatus: false,
      //   },
      appointmentId,
      ...(notificationId && { notificationId }),
      // vin,
      reviewType: notShowUp ? "notShowUp" : "showUp",
      reviewStatus: false,
      conversationID: customerId,
      reviewNotes: isSendReview && text ? text : "",
      reviewRequired: isSendReview,
    };

    // post(data, "salesagent-MaxReview")
    post(data, "salesagent-MaxReview-v2/appointment-status")
      .then((res) => {
        console.log(res);
        setState("purchase");
        onClose();
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  }, [
    appointmentId,
    customerId,
    isSendReview,
    notShowUp,
    notificationId,
    onClose,
    submitLoading,
    text,
    uid,
  ]);

  const renderContent = useMemo(() => {
    return (
      <Box sx={{ width: "100%" }}>
        {state === "purchase" ? (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant="body1" fontWeight={400}>
              Has the customer purchased a vehicle?
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box>
              <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
                <Checkbox
                  checked={isSendReview}
                  onChange={() => setIsSendReview(!isSendReview)}
                  sx={{ padding: "0", "& .MuiSvgIcon-root": { fill: "#297EFF" } }}
                />
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                  Do you want to send a follow-up for this customer?
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#64748B" }}>
                This follow-up review may encourage the customer to return and increase the
                likelihood of a purchase.
              </Typography>
            </Box>
            {isSendReview && (
              <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
                  You can tell the AI what to send for the follow-up.
                </Typography>
                <Textarea
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Write for A.I"
                  rows={4}
                  sx={{ borderRadius: "8px", paddingTop: "16px" }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }, [handleTextChange, isSendReview, state, text]);

  const renderActions = useMemo(
    () =>
      state === "purchase" ? (
        <Box
          width={"100%"}
          sx={{ display: "flex", flexDirection: "row", gap: "8px", justifyContent: "flex-end" }}
        >
          <Button
            onClick={() => {
              setState("follow-up");
            }}
            variant="outlined"
          >
            No
          </Button>

          <Button
            onClick={() => {
              setState("select-car");
            }}
            variant="contained"
            sx={{ backgroundColor: "#2B7FFF" }}
          >
            Yes
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            justifyContent: "flex-end",
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            fullidth
            variant="contained"
            sx={{ backgroundColor: "#2B7FFF", width: "81px", height: "36px" }}
          >
            {submitLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Submit"}
          </Button>
        </Box>
      ),
    [handleSubmit, onClose, state, submitLoading]
  );

  return state === "select-car" ? (
    <SoldReview
      cars={[]}
      defaultState="select-car"
      open={state === "select-car"}
      setOpen={onClose}
      carListMode="search"
      defaultCustomerID={customerId}
      handleClose={onClose}
      notificationId={notificationId}
      appointmentId={appointmentId}
    />
  ) : (
    <Dialog
      open={open}
      handleClose={onClose}
      smallContent={state === "purchase"}
      fullWidth
      maxWidth={state === "purchase" ? "xs" : "md"}
      customWidth={"600px"}
      renderTitle={state === "purchase" ? "Appointment Result" : "Review Request"}
      renderActions={renderActions}
      renderContent={renderContent}
    />
  );
};

export default AppointmentCars;
